/* eslint-disable @typescript-eslint/indent */
import { Box, Button, Checkbox, FormControl, FormErrorMessage, FormLabel, Text, useToast, VStack } from "@chakra-ui/react"
import { useFormik } from "formik"
import React, { FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import * as yup from "yup"
import Lazy from "yup/lib/Lazy"
import Reference from "yup/lib/Reference"
import { ExtendedUserFragment, usePermissionsGroupsQuery, useUpdateUserPermissionsGroupsMutation } from "../graphql"

type UpdateUserPermissionsGroupsFormValues = {
	permissionsGroupIds: string[]
}

const validationSchema = yup.object<Record<keyof UpdateUserPermissionsGroupsFormValues, yup.AnySchema<any, any, any> | Reference<unknown> | Lazy<any, any>>>({
	permissionsGroupIds: yup.array(yup.string()).required().label("Permissions Groups"),
})

export type UpdateUserPermissionsGroupsFormProps = {
	user: ExtendedUserFragment
	userPermissionsGroupIds: string[]
}

export const UpdateUserPermissionsGroupsForm: React.FC<UpdateUserPermissionsGroupsFormProps> = ({ user, userPermissionsGroupIds }) => {
	const initialValues: UpdateUserPermissionsGroupsFormValues = {
		permissionsGroupIds: userPermissionsGroupIds,
	}

	const [{ fetching }, updateUserPermissionsGroups] = useUpdateUserPermissionsGroupsMutation()

	const toast = useToast()
	const navigate = useNavigate()

	const onSubmit = async ({ permissionsGroupIds }: UpdateUserPermissionsGroupsFormValues) => {
		const { data, error } = await updateUserPermissionsGroups({ userId: user._id, permissionsGroupIds })

		if (error) {
			return toast({
				description: error.message.replace("[GraphQL] ", ""),
				status: "error",
			})
		}

		if (data?.updateUserPermissionsGroups) {
			navigate(`/users/${user._id}`, { replace: true })

			return
		}
	}

	const formik = useFormik<UpdateUserPermissionsGroupsFormValues>({ initialValues, validationSchema, onSubmit })

	const [{ data: groupsData, error: groupsError, fetching: groupsFetching }] = usePermissionsGroupsQuery()

	return (
		<VStack as="form" onSubmit={(e) => formik.handleSubmit(e as unknown as FormEvent<HTMLFormElement>)} w="full" align="stretch" spacing={6}>
			<VStack w="full" align="stretch">
				<FormControl isInvalid={Boolean(formik.touched.permissionsGroupIds && formik.errors.permissionsGroupIds)}>
					<FormLabel fontWeight="bold">Permissions Groups</FormLabel>

					{groupsFetching ? (
						<Text>Fetching groups</Text>
					) : groupsError ? (
						<VStack>
							<Text>Couldn&apos;t fetch groups</Text>
							<Text>{groupsError.message.replace("[GraphQL] ", "")}</Text>
						</VStack>
					) : !groupsData?.permissionsGroups.length ? (
						<VStack>
							<Text>Couldn&apos;t fetch groups</Text>
						</VStack>
					) : (
						<Box
							maxH="300px"
							overflowY="auto"
							p={4}
							borderRadius="md"
							bgColor="grayscale.input-background"
						>
							<VStack align="stretch" spacing={2}>
								{groupsData.permissionsGroups.map((group) => (
									<Checkbox
										key={group._id}
										isChecked={formik.values.permissionsGroupIds.includes(group._id)}
										onChange={(e) => {
											const isChecked = e.target.checked
											const currentIds = formik.values.permissionsGroupIds
											const newIds = isChecked
												? [...currentIds, group._id]
												: currentIds.filter((id) => id !== group._id)
											formik.setFieldValue("permissionsGroupIds", newIds)
										}}
									>
										{group.label.name}
									</Checkbox>
								))}
							</VStack>
						</Box>
					)}

					<FormErrorMessage>{formik.errors.permissionsGroupIds}</FormErrorMessage>
				</FormControl>
			</VStack>
			<Button type="submit" colorScheme="primary" isLoading={fetching}>
				Update
			</Button>
		</VStack>
	)
}
